const normalizeText = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase();

const getObjectValues = (value) => {
  if (!value) return [];
  if (value instanceof Map) return Array.from(value.values());
  return Object.values(value);
};

const getObjectKeys = (value) => {
  if (!value) return [];
  if (value instanceof Map) return Array.from(value.keys());
  return Object.keys(value);
};

const getLanguageTerms = (languageOption) => {
  const locale = normalizeText(languageOption?.locale || '').replace('_', '-');
  const localeBase = locale.split('-')[0];

  return [
    languageOption?.code,
    languageOption?.label,
    languageOption?.nativeLabel,
    locale,
    localeBase,
  ]
    .map(normalizeText)
    .filter(Boolean);
};

const matchesCountryLanguage = (countryLanguageNames, languageOption) => {
  const terms = getLanguageTerms(languageOption);

  return countryLanguageNames.some((countryLanguage) =>
    terms.some(
      (term) =>
        term === countryLanguage ||
        countryLanguage.includes(term) ||
        term.includes(countryLanguage)
    )
  );
};

export const sortLanguagesAlpha = (languageOptions = [], locale = 'en') =>
  [...languageOptions].sort((a, b) =>
    (a?.label || a?.code || '').localeCompare(b?.label || b?.code || '', locale, {
      sensitivity: 'base',
    })
  );

export const sortCurrenciesAlpha = (currencyOptions = [], locale = 'en') =>
  [...currencyOptions].sort((a, b) =>
    (a?.label || a?.code || '').localeCompare(b?.label || b?.code || '', locale, {
      sensitivity: 'base',
    })
  );

export const countrySpeaksLanguage = (country, languageCode, languageOptions = []) => {
  if (!country || !languageCode) return false;

  const countryLanguageNames = getObjectValues(country.languages).map(normalizeText).filter(Boolean);
  if (!countryLanguageNames.length) return false;

  const preferredOption = languageOptions.find((option) => {
    const terms = getLanguageTerms(option);
    const normalizedCode = normalizeText(languageCode).replace('_', '-');
    const normalizedBase = normalizedCode.split('-')[0];
    return terms.includes(normalizedCode) || terms.includes(normalizedBase);
  });

  if (!preferredOption) return false;
  return matchesCountryLanguage(countryLanguageNames, preferredOption);
};

export const sortCountriesByLanguage = (
  countryOptions = [],
  languageCode = '',
  languageOptions = [],
  locale = 'en'
) =>
  [...countryOptions].sort((a, b) => {
    const aPriority = countrySpeaksLanguage(a, languageCode, languageOptions) ? 0 : 1;
    const bPriority = countrySpeaksLanguage(b, languageCode, languageOptions) ? 0 : 1;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    return (a?.name?.common || '').localeCompare(b?.name?.common || '', locale, {
      sensitivity: 'base',
    });
  });

export const getDefaultsFromCountry = (
  country,
  languageOptions = [],
  currencyOptions = [],
  preferredLanguageCode = ''
) => {
  if (!country) {
    return { language: '', currency: '' };
  }

  const countryLanguageNames = getObjectValues(country.languages).map(normalizeText).filter(Boolean);
  const countryCurrencyCodes = getObjectKeys(country.currencies).map(normalizeText).filter(Boolean);

  const preferredLanguages = sortLanguagesAlpha(languageOptions).sort((a, b) => {
    const aPreferred = getLanguageTerms(a).includes(normalizeText(preferredLanguageCode));
    const bPreferred = getLanguageTerms(b).includes(normalizeText(preferredLanguageCode));
    return Number(bPreferred) - Number(aPreferred);
  });

  const matchedLanguage = preferredLanguages.find((option) =>
    matchesCountryLanguage(countryLanguageNames, option)
  );

  const matchedCurrency = currencyOptions.find((option) =>
    countryCurrencyCodes.includes(normalizeText(option?.code))
  );

  return {
    language: matchedLanguage?.code || '',
    currency: matchedCurrency?.code || '',
  };
};
